// =============================================================================
// Sensor Status Types (All 9 statuses)
// =============================================================================

/**
 * All sensor status codes from MongoDB V3 API.
 * 0=Unmanaged, 1=Active, 2=Too Low, 3=Too High, 4=To Acknowledge,
 * 5=Inactive, 6=Maintenance, 8=Inhibited, 9=Archived
 */
export type SensorStatus = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 9;

export const ALL_SENSOR_STATUS_MAP: Record<
  SensorStatus,
  { label: string; bgColor: string; textColor: string }
> = {
  0: { label: 'Unmanaged', bgColor: 'bg-gray-100', textColor: 'text-gray-700' },
  1: { label: 'Active', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  2: { label: 'Too Low', bgColor: 'bg-blue-100', textColor: 'text-blue-700' },
  3: { label: 'Too High', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
  4: { label: 'To Acknowledge', bgColor: 'bg-purple-100', textColor: 'text-purple-700' },
  5: { label: 'Inactive', bgColor: 'bg-red-100', textColor: 'text-red-700' },
  6: { label: 'Maintenance', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700' },
  8: { label: 'Inhibited', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
  9: { label: 'Archived', bgColor: 'bg-slate-100', textColor: 'text-slate-700' },
};

export const ALL_SENSOR_STATUS_CODES: SensorStatus[] = [0, 1, 2, 3, 4, 5, 6, 8, 9];

// =============================================================================
// Sensor Type (V3 populated response shape)
// =============================================================================

export interface Sensor {
  _id: string;
  name: string;
  status: SensorStatus;
  value: string | number | null;
  unit: string | null;
  timestamp: string | null;
  organisation: { _id: string; name: string } | string | null;
  /** PostgreSQL account UUID — used for linking to /accounts/:id */
  accountId: string | null;
  site: { _id: string; name: string } | string | null;
  location: { _id: string; name: string } | string | null;
  sensortype: { _id: string; name: string } | string | null;
  usergroup: { _id: string; name: string } | string | null;
  stream_name: string | null;
  billable: boolean;
  customid: string | null;
  input_number: string | number | null;
  tags: string[];
  imageUrl: string | null;
}

// =============================================================================
// Filter Types
// =============================================================================

export interface SensorsFilters {
  name?: string;
  streamName?: string;
  siteLocation?: string;
  currentValue?: string;
  sensorTypeName?: string;
  usergroupName?: string;
  customId?: string;
  inputNumber?: string;
  tags?: string;
  lastReadingFrom?: string;
  lastReadingTo?: string;
  status_in?: number[];
  accountId?: string;
  billable?: boolean;
  hasImage?: boolean;
  sortKey?: string;
  sortDirection?: 1 | -1;
  pageNumber?: number;
  pageLength?: number;
}

export interface SensorAccountFilterOption {
  _id: string;
  name: string;
  accountNumber: string | null;
}

export interface SensorAccountFilterPage {
  accounts: SensorAccountFilterOption[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

export interface SensorStreamOption {
  _id: string;
  name: string;
  transmitter?: string | null;
  transmitterName?: string | null;
}

export interface SensorStreamOptionPage {
  streams: SensorStreamOption[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

// =============================================================================
// Response Types
// =============================================================================

export interface SensorsResponse {
  sensors: Sensor[];
  total: number;
  pageNumber: number;
  pageLength: number;
}

// =============================================================================
// Edit Sensor Types
// =============================================================================

/** Sensor data as returned by GET /v4/legacy/v2/sensors/:id/edit. Field names match MongoDB. */
export interface SensorEditData {
  _id: string;
  name: string;
  customid?: string;
  input_number?: string | number;
  status: number;
  billable?: boolean;
  maintenance?: boolean;
  archived?: boolean;
  /** #1182 — EFFECTIVE calibration scope: the stored override, or the account tag. */
  calibrationIncluded?: boolean;
  /** #1182 — true when no per-sensor override is stored, so the value is inherited. */
  calibrationIsInherited?: boolean;
  /**
   * #1118 — RAW per-sensor on-site exception: 'onsite' | 'ship' | null.
   * null means "no exception, follow the account's Onsite calibration flag".
   */
  onsiteCalibrationOverride?: 'onsite' | 'ship' | null;
  timestamp?: string;
  first_data_at?: string;
  stream?: string;
  stream_name?: string;
  sensortype?: string;
  usergroup?: string;
  site?: string;
  location?: string;
  manufacturer?: string;
  model?: string;
  serialnumber?: string;
  tags?: string[];
  description?: string;
  invoices?: string[];
  sensorgroups?: string[];
  dailylog?: {
    am?: number | null;
    pm?: number | null;
    threshold_min?: number | null;
    threshold_max?: number | null;
  };
  value?: string | number | null;
  organisation?: string;
}

/** Dropdown options returned alongside sensor edit data */
export interface SensorEditOptions {
  sensortypes: Array<{ _id: string; name: string }>;
  usergroups: Array<{ _id: string; name: string }>;
  sites: Array<{ _id: string; name: string }>;
  locations: Array<{ _id: string; name: string; site?: string }>;
  streams: SensorStreamOption[];
  transmitters: Array<{ _id: string; name: string; dev_id?: string }>;
  sensorgroups?: Array<{ _id: string; name: string }>;
}

/** Daily log schedule nested object */
export interface DailyLogForm {
  am: string; // UTC hour 0-23 as string, '' = unset
  pm: string; // UTC hour 0-23 as string, '' = unset
  threshold_min: string; // '' = unset
  threshold_max: string; // '' = unset
}

/** Form state for the edit sensor dialog */
export interface SensorEditForm {
  name: string;
  customid: string;
  input_number: string;
  sensortype: string;
  usergroup: string;
  site: string;
  location: string;
  stream: string;
  billable: boolean;
  maintenance: boolean;
  archived: boolean;
  /**
   * #1182 — per-sensor calibration scope. Held as a plain boolean because the
   * control is a checkbox; the stored column is tri-state (NULL = inherit). The
   * form is seeded with the EFFECTIVE value, so an untouched checkbox produces no
   * diff and nothing is written — the sensor keeps inheriting. Toggling it writes
   * an explicit true/false, which is the right semantics for "someone decided".
   */
  calibrationIncluded: boolean;
  /**
   * #1118 — per-sensor on-site exception, held as a 3-value string because a checkbox
   * cannot express "inherit". 'inherit' is the UI sentinel for a stored NULL; api.ts
   * maps it back to null on save.
   */
  onsiteCalibrationOverride: 'inherit' | 'onsite' | 'ship';
  manufacturer: string;
  model: string;
  serialnumber: string;
  tags: string[];
  description: string;
  invoices: string[];
  sensorgroups: string[];
  dailylog: DailyLogForm;
}

/** Form state for the create sensor dialog */
export interface SensorCreateForm {
  name: string;
  customid: string;
  input_number: string;
  organisation?: string;
  sensortype: string;
  usergroup: string;
  site: string;
  location: string;
  billable: boolean;
  maintenance: boolean;
  manufacturer: string;
  model: string;
  serialnumber: string;
  tags: string[];
  description: string;
  invoices: string[];
  sensorgroups: string[];
  dailylog: DailyLogForm;
}

/** Invoice search result from V3 API */
export interface InvoiceSearchResult {
  _id: string;
  invoice_number: string;
  project_name?: string;
  status?: string;
  expiration_date?: string;
}
